import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Lock, Sparkles, UserPlus, X } from 'lucide-react';
import { fetchWorkerWorkload, quoteAssignment } from '../../api/resources';
import { useCompanySettings } from '../../hooks/useCompanySettings';
import { inr, fmtDate } from '../../utils/format';
import { WORK_TYPES } from './AssignmentEditor';

const DOT = { free: 'bg-success-500', ok: 'bg-warning-500', busy: 'bg-danger-500' };
const LOAD_WORD = { free: 'Free', ok: 'Some work', busy: 'Busy' };
const STATUS = [
  { value: 'ASSIGNED', label: 'To start' },
  { value: 'IN_PROGRESS', label: 'Working' },
  { value: 'COMPLETED', label: 'Done' },
];

// "3 jobs · 5 pcs · due 24-09-2026" — the earliest delivery date among the
// work already on this person's hands.
const loadLine = (w) => [
  `${w.openJobs} job${w.openJobs === 1 ? '' : 's'}`,
  w.pieces ? `${w.pieces} pcs` : null,
  w.nextDue ? `due ${fmtDate(w.nextDue)}` : null,
  w.overdue ? `${w.overdue} late` : null,
].filter(Boolean).join(' · ');

const loadTitle = (w) => (w.openJobs
  ? `${w.openJobs} job(s) open, ${w.pieces} piece(s)${w.nextDue ? `. Earliest delivery among them: ${fmtDate(w.nextDue)}` : ''}${w.overdue ? `. ${w.overdue} already past delivery date.` : ''}`
  : 'Nothing on hand right now');

/**
 * Handing work out, in one place: every garment on the order with its cutting
 * and stitching row, and — next to each name — how loaded that person already
 * is, so the choice is informed without anyone having to open another screen.
 *
 * Deliberately shallow: pick a worker, see the cost, set the status. Rates and
 * overrides stay where they were; nothing new to learn.
 */
// Skills are given on the catalogue item, and a set counts for every part of
// it — so a line matches on its own garment or on the set it came in.
const hasSkill = (w, line) => {
  const ids = w.skillItemIds || [];
  return [line?.tailoringItemId, line?.setItemId].some((id) => id && ids.includes(Number(id)));
};

const CUTTING = WORK_TYPES.find((t) => t.value === 'CUTTING');
const STITCHING = WORK_TYPES.find((t) => t.value === 'STITCHING');

export default function WorkerAllocation({ items, onChangeLine, canOverride }) {
  const { data } = useQuery({ queryKey: ['worker-workload'], queryFn: fetchWorkerWorkload });
  const workers = data?.workers || [];

  // Whether the cloth is cut in one go by one cutting master, or garment by
  // garment. The shop says which on Settings; both ways are real, and a shop
  // that cuts a coat and a shirt separately should not be made to pretend
  // otherwise.
  const { singleCuttingMaster } = useCompanySettings();

  // Cutting gets one row per garment either way, carrying that garment's own
  // status — what changes is whether that row has a picker of its own or
  // follows the one above. Stitching is different: a line of "Shirt x 3" is
  // three garments that may go to three different tailors, so it gets a row
  // per piece.
  const rows = useMemo(() => items.flatMap((line) => {
    const pieces = Math.max(1, Number(line.qty) || 1);
    return [
      { line, type: CUTTING, pieceNo: 1, pieces, shared: singleCuttingMaster, first: true },
      ...Array.from({ length: pieces }, (_, p) => ({ line, type: STITCHING, pieceNo: p + 1, pieces, perPiece: true })),
    ];
  }), [items, singleCuttingMaster]);

  const sameSlot = (a, type, pieceNo) => a.workType === type.value && (a.pieceNo || 1) === pieceNo;
  const assignmentOf = (line, type, pieceNo) => (line.assignments || []).find((a) => sameSlot(a, type, pieceNo)) || null;

  const setAssignment = (line, type, pieceNo, next) => {
    const rest = (line.assignments || []).filter((a) => !sameSlot(a, type, pieceNo));
    onChangeLine({
      ...line,
      assignments: next ? [...rest, { ...next, workType: type.value, pieceNo }] : rest,
    });
  };

  // Who is cutting this order. One person for all of it, so the picker shows
  // whoever the garments already point at — and says so when they disagree,
  // which can only happen on an order assigned before this rule existed.
  const cutters = [...new Set(items
    .map((l) => (l.assignments || []).find((a) => a.workType === 'CUTTING')?.workerId)
    .filter(Boolean)
    .map(String))];
  const cuttingWorkerId = cutters.length === 1 ? Number(cutters[0]) : null;
  const cuttingSplit = cutters.length > 1;
  const cuttingLocked = items.some((l) => (l.assignments || [])
    .some((a) => a.workType === 'CUTTING' && a.settled));

  // Setting it writes the same worker onto every garment, keeping each one's
  // own status untouched.
  const setCuttingWorker = (w) => {
    items.forEach((line) => {
      const rest = (line.assignments || []).filter((a) => a.workType !== 'CUTTING');
      const prev = (line.assignments || []).find((a) => a.workType === 'CUTTING');
      if (prev?.settled) return; // settled work is frozen
      onChangeLine({
        ...line,
        assignments: [...rest, {
          ...(prev || { status: 'ASSIGNED' }),
          workType: 'CUTTING',
          pieceNo: 1,
          workerId: String(w.id),
          workerName: w.name,
          calculatedCost: null,
          ...(Number(prev?.workerId) !== w.id ? { overrideCost: null, overrideReason: '' } : {}),
        }],
      });
    });
  };

  const clearCuttingWorker = () => {
    items.forEach((line) => {
      const prev = (line.assignments || []).find((a) => a.workType === 'CUTTING');
      if (prev?.settled) return;
      onChangeLine({ ...line, assignments: (line.assignments || []).filter((a) => a.workType !== 'CUTTING') });
    });
  };

  // Fill every empty slot with the least-loaded person who does that work.
  const autoAssign = () => {
    const busy = new Map(workers.map((w) => [w.id, w.pieces]));
    const next = items.map((line) => {
      const assignments = [...(line.assignments || [])];
      const pieces = Math.max(1, Number(line.qty) || 1);
      // Cutting: one row per garment. With a single cutting master the whole
      // order follows whoever is already chosen above; otherwise each garment
      // is filled on its own, preferring someone who makes that garment —
      // the same way stitching is filled.
      if (!assignments.some((a) => sameSlot(a, CUTTING, 1) && a.workerId)) {
        const already = singleCuttingMaster && cuttingWorkerId
          ? workers.find((w) => w.id === cuttingWorkerId)
          : null;
        const cutters = workers.filter((w) => CUTTING.roles.includes(w.role));
        const skilled = singleCuttingMaster ? [] : cutters.filter((w) => hasSkill(w, line));
        const pool = skilled.length ? skilled : cutters;
        const pick = already || (pool.length ? pool : workers)
          .slice()
          .sort((a, b) => (busy.get(a.id) || 0) - (busy.get(b.id) || 0))[0];
        if (pick) {
          busy.set(pick.id, (busy.get(pick.id) || 0) + pieces);
          assignments.push({
            workType: 'CUTTING', pieceNo: 1, workerId: String(pick.id), workerName: pick.name, status: 'ASSIGNED',
          });
        }
      }
      // Stitching: one row per piece, each free to land on someone else.
      for (let pieceNo = 1; pieceNo <= pieces; pieceNo += 1) {
        if (assignments.some((a) => sameSlot(a, STITCHING, pieceNo) && a.workerId)) continue;
        const tailors = workers.filter((w) => STITCHING.roles.includes(w.role));
        const skilled = tailors.filter((w) => hasSkill(w, line));
        const pool = skilled.length ? skilled : tailors;
        const pick = (pool.length ? pool : workers)
          .slice()
          .sort((a, b) => (busy.get(a.id) || 0) - (busy.get(b.id) || 0))[0];
        if (!pick) continue;
        busy.set(pick.id, (busy.get(pick.id) || 0) + 1);
        assignments.push({
          workType: 'STITCHING', pieceNo, workerId: String(pick.id), workerName: pick.name, status: 'ASSIGNED',
        });
      }
      return { ...line, assignments };
    });
    next.forEach((line) => onChangeLine(line));
  };

  const unassigned = rows.filter(({ line, type, pieceNo }) => !assignmentOf(line, type, pieceNo)?.workerId).length;

  if (!items.length) {
    return <p className="rounded-md border border-dashed border-line-strong py-5 text-center text-xs italic text-content-subtle">Add a garment first — then hand its cutting and stitching to someone.</p>;
  }

  return (
    // The worker list is a portal now and places itself against the viewport,
    // so the panel no longer reserves room underneath for it — that reserve
    // was 19rem of blank space, which is most of a phone screen.
    <div>
      {/* Cutting is decided once, here, for the whole order — unless the shop
          cuts garment by garment, and then each row below asks for itself. */}
      {singleCuttingMaster && (
      <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-sunken px-2.5 py-2">
        <span className="text-xs font-semibold text-content">Cutting for the whole order</span>
        <span className="min-w-[11rem] flex-1 sm:max-w-[18rem]">
          <WorkerPicker
            workers={workers}
            type={CUTTING}
            value={cuttingWorkerId}
            fallbackName={cuttingSplit ? 'Different cutters — pick one' : undefined}
            onPick={setCuttingWorker}
            onClear={clearCuttingWorker}
          />
        </span>
        {cuttingLocked && (
          <span className="text-2xs text-content-subtle">Some cutting is settled and stays as it is.</span>
        )}
        <span className="w-full text-2xs text-content-subtle sm:w-auto">
          Each garment still gets its own cutting status below.
        </span>
      </div>
      )}

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="mr-auto text-xs text-content-muted">
          {unassigned
            ? <><b className="text-danger-600">{unassigned}</b> job{unassigned === 1 ? '' : 's'} still to hand out</>
            : <span className="font-semibold text-success-700">Everything is handed out</span>}
        </p>
        {unassigned > 0 && (
          <button type="button" className="btn-outline btn-sm" onClick={autoAssign} title="Give each empty job to the least busy suitable worker">
            <Sparkles size={13} /> Auto-assign the rest
          </button>
        )}
      </div>

      {/* The worker list is drawn in a portal, so a scroll container here can
          no longer clip it — which is what a five-column table needs on a
          narrow screen. */}
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[38rem] text-sm">
          <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
            <tr>
              <th className="px-2 py-1.5">Garment</th>
              <th className="px-2 py-1.5">Work</th>
              <th className="px-2 py-1.5">Worker</th>
              <th className="px-2 py-1.5 text-right">Pay</th>
              <th className="px-2 py-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ line, type, pieceNo, pieces, shared, first, perPiece }) => {
              const entry = assignmentOf(line, type, pieceNo);
              // Name the garment on its cutting row — the one row every line
              // has — and leave the stitching rows under it unnamed.
              const firstOfPiece = !!first;
              return (
                <AllocRow
                  key={`${line.localKey}-${pieceNo}-${type.value}`}
                  line={line}
                  type={type}
                  pieceNo={pieceNo}
                  pieces={pieces}
                  shared={shared}
                  perPiece={perPiece}
                  entry={entry}
                  workers={workers}
                  canOverride={canOverride}
                  showItem={firstOfPiece}
                  onChange={(next) => setAssignment(line, type, pieceNo, next)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AllocRow({ line, type, pieceNo, pieces, shared, perPiece, entry, workers, canOverride, showItem, onChange }) {
  // With worker-wise rates off there is one rate per item, so there is nothing
  // to set here — the cost is simply shown.
  const { workerWiseRates } = useCompanySettings();
  const mayPriceHere = canOverride && workerWiseRates;
  const locked = !!entry?.settled;
  const workerId = entry?.workerId ? Number(entry.workerId) : null;
  const qty = Number(line.qty) || 1;
  const itemId = line.tailoringItemId ? Number(line.tailoringItemId) : null;

  // Live rate-card cost, so the figure is known before the order is saved.
  // One row is one garment, so it is always priced for a single piece — the
  // line's quantity is spread across its pieces, not charged to each of them.
  const quote = useQuery({
    queryKey: ['assign-quote', workerId, itemId, type.value, 1],
    queryFn: () => quoteAssignment({ workerId, tailoringItemId: itemId, workType: type.value, qty: 1 }),
    enabled: !!workerId && !!itemId && !locked,
  });
  const calculated = locked ? entry.calculatedCost : (quote.data?.calculatedCost ?? entry?.calculatedCost ?? null);
  const overridden = entry?.overrideCost !== '' && entry?.overrideCost != null;
  const cost = overridden ? Number(entry.overrideCost) : calculated;

  const patch = (p) => onChange({ ...(entry || { status: 'ASSIGNED' }), pieceNo, ...p });
  const chosen = workers.find((w) => w.id === workerId);

  return (
    <tr className="border-t border-line align-middle">
      <td className="px-2 py-1.5">
        {showItem ? (
          <span className="font-semibold text-content">
            {line.itemName}
            {/* A one-piece line reads as it always did; a longer one names the
                garment being handed out, because each goes its own way. */}
            <span className="font-normal text-content-subtle"> × {qty}</span>
          </span>
        ) : <span className="text-content-subtle">·</span>}
      </td>
      <td className="px-2 py-1.5 text-content-muted">
        {type.label}
        {/* Which garment this stitching row is for. Cutting is one row for the
            whole line however it is handed out, so it never carries a piece
            number — there is nothing to tell apart. */}
        {perPiece && pieces > 1 && <span className="ml-1 text-2xs text-content-subtle">#{pieceNo} of {pieces}</span>}
      </td>
      <td className="px-2 py-1.5">
        {locked ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700">
            <Lock size={12} /> {entry.workerName} · {entry.settlementNo || 'settled'}
          </span>
        ) : shared ? (
          // Cutting is chosen once for the order, in the bar above — showing a
          // picker on every line would invite exactly the disagreement the
          // single control exists to prevent.
          entry?.workerName
            ? <span className="text-sm text-content">{entry.workerName}</span>
            : <span className="text-xs italic text-content-subtle">Pick a cutter above</span>
        ) : (
          <WorkerPicker
            workers={workers}
            type={type}
            line={line}
            value={workerId}
            fallbackName={entry?.workerName}
            onPick={(w) => patch({
              workerId: String(w.id), workerName: w.name, calculatedCost: null,
              ...(w.id !== workerId ? { overrideCost: null, overrideReason: '' } : {}),
            })}
            onClear={() => onChange(null)}
          />
        )}
        {chosen && !locked && (
          <span className="mt-0.5 block text-2xs text-content-subtle" title={loadTitle(chosen)}>{LOAD_WORD[chosen.load]} — {loadLine(chosen)}</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {!workerId ? <span className="text-content-subtle">—</span> : mayPriceHere && !locked ? (
          <input
            type="number" min={0} step="0.01"
            className="input !w-20 !py-1 text-right tabular-nums sm:!w-24"
            placeholder={calculated != null ? String(calculated) : 'Rate'}
            title="Rate-card cost; type here to override it"
            value={overridden ? entry.overrideCost : ''}
            onChange={(e) => patch({ overrideCost: e.target.value === '' ? null : e.target.value })}
          />
        ) : (
          <span className="font-semibold tabular-nums">{cost != null ? inr(cost) : '…'}</span>
        )}
        {workerId && mayPriceHere && !locked && calculated != null && (
          <span className="mt-0.5 block text-2xs text-content-subtle">card {inr(calculated)}</span>
        )}
      </td>
      <td className="px-2 py-1.5">
        {locked ? <span className="text-2xs font-semibold text-brand-700">Settled</span> : (
          <select
            className="input !py-1"
            value={entry?.status || 'ASSIGNED'}
            disabled={!workerId}
            onChange={(e) => patch({ status: e.target.value })}
            aria-label={`${type.label} status`}
          >
            {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        )}
      </td>
    </tr>
  );
}

// Picks a person, showing what each already has on their hands.
function WorkerPicker({ workers, type, line, value, fallbackName, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [box, setBox] = useState(null); // where to draw the list, in screen coords
  const ref = useRef(null);
  const panelRef = useRef(null);
  useEffect(() => {
    const away = (e) => {
      if (ref.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  // The panel is drawn in a portal so the table around it can scroll sideways
  // without clipping it. That means placing it by hand: under the button, as
  // wide as there is room for, and flipped up when the bottom is close.
  const place = useCallback(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(288, window.innerWidth - 16);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < 200 && above > below;
    setBox({
      left,
      width,
      top: up ? undefined : r.bottom + 4,
      bottom: up ? window.innerHeight - r.top + 4 : undefined,
      maxHeight: Math.max(140, (up ? above : below)),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const on = () => place();
    window.addEventListener('scroll', on, true);
    window.addEventListener('resize', on);
    return () => {
      window.removeEventListener('scroll', on, true);
      window.removeEventListener('resize', on);
    };
  }, [open, place]);

  // Suitable first, and within each group the least loaded first. Somebody
  // with no tailoring trade — a supervisor, a manager — is left out entirely
  // rather than offered as "other trade": they do not make garments, and the
  // server refuses the job anyway. One already on the row stays, so an older
  // order still reads correctly.
  const sorted = useMemo(() => {
    const byLoad = (a, b) => a.pieces - b.pieces || a.name.localeCompare(b.name);
    const makers = workers.filter((w) => w.role !== 'NO_TRADE' || w.id === value);
    const fits = makers.filter((w) => type.roles.includes(w.role)).sort(byLoad);
    const rest = makers.filter((w) => !type.roles.includes(w.role)).sort(byLoad);
    return [...fits, ...rest];
  }, [workers, type, value]);
  // For a garment, only the people who make it — unless nobody has that
  // skill yet, or the user asks for everyone. The chosen worker always stays.
  const skilled = useMemo(
    () => (line ? sorted.filter((w) => hasSkill(w, line)) : []),
    [sorted, line],
  );
  const narrowed = !!line && !showAll && skilled.length > 0;
  // Showing everyone does not bury the people who make this garment: they
  // stay at the top, marked, and the rest follow. Opening the list up is for
  // when nobody skilled is free — not a reason to start hunting again. Sort
  // is stable, so inside each group the trade-and-load order is untouched.
  const shown = narrowed
    ? sorted.filter((w) => hasSkill(w, line) || w.id === value)
    : (skilled.length
      ? [...sorted].sort((a, b) => (hasSkill(b, line) ? 1 : 0) - (hasSkill(a, line) ? 1 : 0))
      : sorted);
  const best = shown[0];
  const chosen = workers.find((w) => w.id === value);
  const label = chosen?.name || fallbackName || null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { if (!open) place(); setOpen((o) => !o); }}
        className={`flex w-full min-w-[9.5rem] items-center gap-1.5 rounded-md border px-2 py-1 text-left text-sm sm:min-w-[11rem] ${label ? 'border-line bg-surface' : 'border-dashed border-brand-300 text-brand-700'}`}
      >
        {chosen && <span className={`h-2 w-2 shrink-0 rounded-full ${DOT[chosen.load]}`} />}
        <span className="truncate">{label || <><UserPlus size={13} className="mr-1 inline" />Assign someone</>}</span>
        <span className="ml-auto text-content-subtle">▾</span>
      </button>

      {open && box && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', left: box.left, top: box.top, bottom: box.bottom, width: box.width }}
          className="z-[70] overflow-hidden rounded-lg border border-line bg-surface shadow-pop"
        >
          <ul style={{ maxHeight: Math.min(256, box.maxHeight) }} className="overflow-y-auto py-1">
            {label && (
              <li>
                <button type="button" className="w-full px-3 py-1.5 text-left text-xs text-danger-600 hover:bg-danger-50" onClick={() => { onClear(); setOpen(false); }}>
                  <X size={12} className="mr-1 inline" /> Remove this assignment
                </button>
              </li>
            )}
            {line && !skilled.length && sorted.length > 0 && (
              <li className="px-3 py-1.5 text-2xs text-warning-700">Nobody has {line.itemName || 'this garment'} as a skill yet — showing everyone.</li>
            )}
            {sorted.length === 0 && <li className="px-3 py-2 text-xs text-content-subtle">No active workers.</li>}
            {shown.map((w) => {
              const fits = type.roles.includes(w.role);
              return (
                <li key={w.id}>
                  <button
                    type="button"
                    className={`flex w-full items-start gap-2 px-3 py-1.5 text-left hover:bg-surface-hover ${w.id === value ? 'bg-brand-50' : ''}`}
                    onClick={() => { onPick(w); setOpen(false); }}
                  >
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT[w.load]}`} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <b className="truncate text-sm font-medium text-content">{w.name}</b>
                        {w.id === best?.id && fits && <span className="rounded bg-success-50 px-1 text-2xs font-bold text-success-700">Freest</span>}
                        {!fits && <span className="rounded bg-surface-sunken px-1 text-2xs text-content-subtle">other trade</span>}
                        {/* Once the list is open to everyone, each row says
                            plainly which side it is on — green for the people
                            who make this garment, amber for the rest. */}
                        {line && skilled.length > 0 && (hasSkill(w, line)
                          ? <span className="rounded bg-success-50 px-1 text-2xs font-bold text-success-700">their skill</span>
                          : <span className="rounded bg-warning-50 px-1 text-2xs text-warning-700">not their skill</span>)}
                        {w.id === value && <Check size={12} className="text-brand-700" />}
                      </span>
                      <span className="block text-2xs text-content-subtle" title={loadTitle(w)}>{loadLine(w)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
            {line && skilled.length > 0 && skilled.length < sorted.length && (
              <li>
                <button type="button" className="w-full px-3 py-1.5 text-left text-2xs font-semibold text-brand-700 hover:bg-surface-hover" onClick={() => setShowAll((v) => !v)}>
                  {showAll ? `Only those skilled in ${line.itemName || 'this garment'}` : `Show all workers (${sorted.length})`}
                </button>
              </li>
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
